import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Camera, Sparkles, Zap, Globe, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export default function LandingPage() {
  const [lang, setLang] = useState<'en' | 'nl'>('en')
  const [images, setImages] = useState<string[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    fetch('/api/carousel')
      .then(res => res.json())
      .then(data => setImages(data))
      .catch(err => console.error('Failed to fetch carousel images', err))
  }, [])

  useEffect(() => {
    if (images.length === 0) return
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [images.length])

  const content = {
    en: {
      title: 'Professional Photo Booth Software',
      subtitle: 'Capture moments, create memories, and engage your audience with PoseBox.',
      getStarted: 'Get Started',
      featuresTitle: 'Powerful Features',
      features: [
        { icon: Camera, title: 'AI Face Tracking', desc: 'Real-time digital props and face tracking for interactive fun.' },
        { icon: Sparkles, title: 'Premium Effects', desc: 'Beauty filters, background replacement, and custom overlays.' },
        { icon: Zap, title: 'Instant Sharing', desc: 'QR codes and email sharing for guests to get their photos instantly.' },
        { icon: Globe, title: '100% Web Based', desc: 'No installations required. Works on any device with a browser, from iPads to large touch screens.' },
      ],
      footer: 'PoseBox v1.0.4 · Built for professionals',
    },
    nl: {
      title: 'Professionele Fotobooth Software',
      subtitle: 'Leg momenten vast, creëer herinneringen en betrek je publiek met PoseBox.',
      getStarted: 'Aan de slag',
      featuresTitle: 'Krachtige Functies',
      features: [
        { icon: Camera, title: 'AI Gezichtsherkenning', desc: 'Real-time digitale props en face tracking voor interactief plezier.' },
        { icon: Sparkles, title: 'Premium Effecten', desc: 'Beauty filters, achtergrondvervanging en aangepaste overlays.' },
        { icon: Zap, title: 'Direct Delen', desc: 'QR-codes en e-mail delen zodat gasten hun foto\'s direct krijgen.' },
        { icon: Globe, title: '100% Webbased', desc: 'Geen installaties nodig. Werkt op elk apparaat met een browser, van iPads tot grote touchscreens.' },
      ],
      footer: 'PoseBox v1.0.4 · Gebouwd voor professionals',
    }
  }

  const c = content[lang]

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0a0a0f] text-white">
      {/* Language Selector */}
      <div className="absolute top-6 right-6 z-20 flex gap-4">
        <button 
          onClick={() => setLang('en')} 
          className={`text-2xl transition-opacity ${lang === 'en' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
          title="English"
        >
          🇬🇧
        </button>
        <button 
          onClick={() => setLang('nl')} 
          className={`text-2xl transition-opacity ${lang === 'nl' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
          title="Nederlands"
        >
          🇳🇱
        </button>
      </div>

      {/* Animated background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          animate={{ x: [0, 60, 0], y: [0, -40, 0], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -left-20 -top-20 h-[500px] w-[500px] rounded-full bg-blue-600/20 blur-[120px]"
        />
        <motion.div
          animate={{ x: [0, -50, 0], y: [0, 60, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-20 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-600/20 blur-[120px]"
        />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-6 max-w-5xl">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-6 flex items-center gap-3"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25">
            <Camera className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            Pose<span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Box</span>
          </h1>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-5xl font-black text-center mb-4 text-white"
        >
          {c.title}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-center text-xl text-white/60 mb-12 max-w-2xl"
        >
          {c.subtitle}
        </motion.p>
        
        {/* Get Started Button (Top) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/login"
            className="group relative inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/25 hover:shadow-primary/40"
          >
            {c.getStarted}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        {/* Grid for Content */}
        <div className="w-full max-w-6xl">
          {/* Content Row */}
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8 items-stretch">
            {/* Left Column: Features */}
            <div className="flex flex-col gap-4">
              <h3 className="text-xl font-bold text-white/80 text-center lg:text-left">{c.featuresTitle}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full content-start">
              {c.features.map((feat, i) => (
                <motion.div
                  key={feat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.1, duration: 0.6 }}
                  className="bg-white/[0.02] border border-white/5 rounded-xl p-4 hover:bg-white/[0.04] transition-colors flex flex-col justify-between"
                >
                  <div>
                    <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center mb-3">
                      <feat.icon className="h-4 w-4 text-blue-400" />
                    </div>
                    <h4 className="text-sm font-semibold mb-1">{feat.title}</h4>
                    <p className="text-xs text-white/40">{feat.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right Column: Carousel */}
            <div className="flex flex-col gap-4 w-full h-full">
              <h3 className="text-xl font-bold text-white/80 text-center lg:text-left">Product Preview</h3>
              {images.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, duration: 0.6 }}
                  className="relative w-full h-full min-h-[300px] lg:min-h-full overflow-hidden rounded-xl border border-white/10 bg-black/30"
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentIndex}
                      src={images[currentIndex]}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 h-full w-full object-contain"
                      alt="Carousel"
                    />
                  </AnimatePresence>
                  
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setCurrentIndex((prev) => (prev + 1) % images.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                        {images.map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 w-1 rounded-full ${i === currentIndex ? 'bg-white' : 'bg-white/30'}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-20 text-xs text-white/20"
        >
          {c.footer}
        </motion.div>
      </div>
    </div>
  )
}
